import React, { useEffect } from 'react'
import { 
  CheckCircle2,
  Mail,
  FileText,
  Clock,
  Download,
  Monitor,
  Settings,
  Shield,
  Info
} from 'lucide-react'
import { useOnboardingStore } from '../../../stores/onboardingStore'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Input } from '../../../components/ui/Input'
import { Label } from '../../../components/ui/Label'
import { Badge } from '../../../components/ui/Badge'
import { ServiceConfigUtils } from '../../../config/services.utils'
import { cn } from '../../../lib/utils'

export const IntelligenceDeliveryScreen: React.FC = () => {
  const { data, updateData } = useOnboardingStore()

  // Get selected service details
  const selectedService = data.selectedService
  const services = ServiceConfigUtils.getActiveServices()
  const serviceDetails = services.find(s => s.id === selectedService?.id)

  // Initialize delivery email with contact email only if not set
  // This preserves user's choice if they've already customized it
  useEffect(() => {
    if (data.contactInfo.email && !data.deliveryPreferences.deliveryEmail) {
      updateData('deliveryPreferences', {
        ...data.deliveryPreferences,
        deliveryEmail: data.contactInfo.email
      })
    }
  }, [data.contactInfo.email, data.deliveryPreferences.deliveryEmail, updateData])

  // Update delivery email only (don't modify contact info)
  const updateDeliveryEmail = (deliveryEmail: string) => {
    updateData('deliveryPreferences', {
      ...data.deliveryPreferences,
      deliveryEmail
    })
  }

  // Get tier badge configuration
  const getTierBadgeConfig = (tierLevel?: string) => {
    switch (tierLevel) {
      case 'quick':
        return { label: 'QUICK', bgClass: 'bg-gradient-to-r from-emerald-500 to-green-500' }
      case 'standard':
        return { label: 'STANDARD', bgClass: 'bg-gradient-to-r from-blue-500 to-cyan-500' }
      case 'comprehensive':
        return { label: 'COMPREHENSIVE', bgClass: 'bg-gradient-to-r from-purple-500 to-pink-500' }
      case 'enterprise':
        return { label: 'ENTERPRISE', bgClass: 'bg-gradient-to-r from-amber-500 to-orange-500' }
      default:
        return { label: 'STANDARD', bgClass: 'bg-gradient-to-r from-blue-500 to-cyan-500' }
    }
  }

  const tierConfig = getTierBadgeConfig(selectedService?.tierLevel)
  const isFree = selectedService?.price === 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Confirm Delivery Details
        </h2>
        <p className="text-gray-600">
          Review your selected service and confirm where you'd like to receive your intelligence report
        </p>
      </div>

      {/* Selected Service Summary */}
      <Card className="bg-green-50 border-green-200">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-lg">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
              Selected Service
            </CardTitle>
            <div className={`${tierConfig.bgClass} text-white rounded-full px-3 py-1 text-xs font-bold`}>
              {tierConfig.label}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {serviceDetails ? (
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-lg text-green-900 mb-1">
                    {serviceDetails.name}
                  </h3>
                  <p className="text-green-800 text-sm mb-3">
                    {serviceDetails.description}
                  </p>
                  <div className="flex items-center gap-4">
                    <span className={`text-xl font-bold ${
                      isFree ? 'text-green-600' : 'text-green-900'
                    }`}>
                      {serviceDetails.price.display}
                    </span>
                    {isFree && (
                      <Badge className="bg-green-500 text-white border-0">
                        FREE - No Credit Card
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* What You'll Receive */}
              <div className="border-t border-green-200 pt-4">
                <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  What You'll Receive
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2 text-green-800">
                    <FileText className="h-4 w-4 text-green-600" />
                    <span>{serviceDetails.metadata.specifications.documents.pageCount.display} comprehensive report</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-800">
                    <Clock className="h-4 w-4 text-green-600" />
                    <span>Delivered within {serviceDetails.metadata.deliveryTime}</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-800">
                    <Download className="h-4 w-4 text-green-600" />
                    <span>
                      {selectedService?.deliveryFormats?.map(f => f.toUpperCase()).join(', ') || 'Multiple'} formats
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-green-800">
                    <Monitor className="h-4 w-4 text-green-600" />
                    <span>Professional analysis and insights</span>
                  </div>
                </div>

                {/* Consulting Value */}
                <div className="mt-4 p-3 bg-white/60 rounded-lg border border-green-200/80">
                  <div className="text-sm text-green-700">
                    <strong>Professional Value:</strong> Replaces {serviceDetails.metadata.specifications.quality.consultingValue} consulting engagement
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-green-800">
              <p>No service selected. Please go back and select a service.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delivery Email Configuration */}
      <Card className="border-2 hover:border-primary/30 transition-colors duration-300">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
            Delivery Email Address
          </CardTitle>
          <CardDescription className="text-base">
            Where should we send your intelligence report? We'll deliver it within 4 hours.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Contact Person Context */}
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-sm text-gray-600">
                <strong>Account Contact:</strong> {data.contactInfo.contactPerson} ({data.contactInfo.email})
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Reports can be delivered to a different email address if needed
              </div>
            </div>
            
            <div>
              <Label htmlFor="delivery-email" className="flex items-center gap-2 font-medium mb-2">
                <Mail className="w-4 h-4 text-gray-500" />
                Email Address *
              </Label>
              <Input
                id="delivery-email"
                type="email"
                value={data.deliveryPreferences.deliveryEmail || data.contactInfo.email}
                onChange={(e) => updateDeliveryEmail(e.target.value)}
                placeholder="your.email@company.com"
                className="h-12 text-base"
                required
              />
              <p className="text-sm text-gray-500 mt-2">
                This is where you'll receive your intelligence report and any important updates.
              </p>
            </div>

            {/* Email Preview */}
            {(data.deliveryPreferences.deliveryEmail || data.contactInfo.email) && (
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-start gap-3">
                  <Settings className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <div className="font-semibold text-blue-800 mb-1">Delivery Confirmed</div>
                    <div className="text-sm text-blue-700">
                      Your {serviceDetails?.name || 'intelligence report'} will be delivered to{' '}
                      <strong>{data.deliveryPreferences.deliveryEmail || data.contactInfo.email}</strong> within 4 hours of completion.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delivery Timeline */}
      <Card className="bg-gradient-to-br from-indigo-50 to-blue-50 border-indigo-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-indigo-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-indigo-800 mb-2">Delivery Timeline</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-2 h-2 bg-indigo-600 rounded-full"></div>
                  <span className="text-indigo-700">
                    <strong>Processing:</strong> Your intelligence report will begin processing immediately after payment
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-2 h-2 bg-indigo-600 rounded-full"></div>
                  <span className="text-indigo-700">
                    <strong>Analysis:</strong> Our AI systems will analyze your business data and market intelligence
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-2 h-2 bg-indigo-600 rounded-full"></div>
                  <span className="text-indigo-700">
                    <strong>Delivery:</strong> Complete report delivered to your email within 4 hours
                  </span>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-white/60 rounded-lg border border-indigo-200/80">
                <div className="flex items-center gap-2 text-sm text-indigo-800 font-medium">
                  <Shield className="w-4 h-4" />
                  All reports include multiple export formats and comprehensive analysis
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Next Steps Info */}
      <Card className="border-2 border-dashed border-gray-300 bg-gray-50">
        <CardContent className="p-6">
          <div className="text-center">
            <h3 className="font-semibold text-gray-800 mb-2">Ready to Continue?</h3>
            <p className="text-sm text-gray-600">
              {isFree 
                ? "Click Continue to complete your free intelligence report setup."
                : `Next, you'll complete payment for your ${serviceDetails?.name || 'selected service'}.`
              }
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}